/**
 * File Picker Modal Component
 *
 * Modal for selecting uploaded files from the fileserver.
 * Can be used to attach files to Canvas nodes or AI workflow inputs.
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Search,
  File,
  FileText,
  FileImage,
  FileVideo,
  FileArchive,
  FileCode,
  FileSpreadsheet,
  Music,
  CheckCircle,
  Circle,
  Loader
} from 'lucide-react';
import { listFiles } from '../api/filesApi';

// File type icon mapping (reused from FileBrowser)
const getFileIcon = (mimeType, filename) => {
  if (!mimeType && filename) {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return FileImage;
    if (['mp4', 'avi', 'mov', 'mkv'].includes(ext)) return FileVideo;
    if (['zip', 'rar', 'tar', 'gz', '7z', 'bz2'].includes(ext)) return FileArchive;
    if (['js', 'jsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json'].includes(ext)) return FileCode;
    if (['mp3', 'wav', 'flac', 'ogg', 'm4a'].includes(ext)) return Music;
    if (['txt', 'md', 'pdf', 'doc', 'docx'].includes(ext)) return FileText;
    if (['xls', 'xlsx', 'xlst', 'csv', 'tsv'].includes(ext)) return FileSpreadsheet;
  }
  if (mimeType) {
    if (mimeType.startsWith('image/')) return FileImage;
    if (mimeType.startsWith('video/')) return FileVideo;
    if (mimeType.startsWith('audio/')) return Music;
    if (mimeType.startsWith('text/')) return FileText;
    if (mimeType.includes('zip') || mimeType.includes('archive')) return FileArchive;
    if (mimeType.includes('application/json') || mimeType.includes('javascript')) return FileCode;
  }
  return File;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * FilePicker Component
 *
 * @param {boolean} show - Whether to show the modal
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelect - Callback when files are selected (files: Array)
 * @param {boolean} multiSelect - Allow selecting multiple files (default: false)
 * @param {string} fileTypeFilter - Filter by file type ('image', 'text', 'video', etc.)
 */
export default function FilePicker({ show, onClose, onSelect, multiSelect = false, fileTypeFilter = null }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Load files on mount
  useEffect(() => {
    if (show) {
      loadFiles();
      setSelectedFiles([]);
    }
  }, [show, fileTypeFilter]);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await listFiles({
        limit: 100,
        offset: 0,
        // 'upload' / 'output' are fileserver-side categories; the rest filter client-side
        file_type: ['upload', 'output'].includes(fileTypeFilter) ? fileTypeFilter : null
      });

      setFiles(response.files || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter files by search query and file type
  const filteredFiles = files.filter((file) => {
    const matchesSearch = file.filename.toLowerCase().includes(searchQuery.toLowerCase());

    if (!fileTypeFilter) return matchesSearch;

    // Apply file type filter
    const mimeType = file.mime_type || '';
    const filename = file.filename || '';

    if (fileTypeFilter === 'image') {
      return matchesSearch && (mimeType.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(filename));
    }
    if (fileTypeFilter === 'text') {
      return matchesSearch && (mimeType.startsWith('text/') || /\.(txt|md|pdf|doc|docx)$/i.test(filename));
    }
    if (fileTypeFilter === 'video') {
      return matchesSearch && (mimeType.startsWith('video/') || /\.(mp4|avi|mov|mkv)$/i.test(filename));
    }
    if (fileTypeFilter === 'audio') {
      return matchesSearch && (mimeType.startsWith('audio/') || /\.(mp3|wav|flac|ogg|m4a)$/i.test(filename));
    }

    return matchesSearch;
  });

  // Handle file selection
  const handleFileClick = (file) => {
    if (multiSelect) {
      // Toggle selection
      const isSelected = selectedFiles.some(f => f.file_id === file.file_id);
      if (isSelected) {
        setSelectedFiles(selectedFiles.filter(f => f.file_id !== file.file_id));
      } else {
        setSelectedFiles([...selectedFiles, file]);
      }
    } else {
      // Single select
      setSelectedFiles([file]);
    }
  };

  // Handle confirm selection
  const handleConfirm = () => {
    if (selectedFiles.length > 0) {
      onSelect(multiSelect ? selectedFiles : selectedFiles[0]);
      onClose();
    }
  };

  // Check if file is selected
  const isFileSelected = (file) => {
    return selectedFiles.some(f => f.file_id === file.file_id);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 flex flex-col" style={{ maxHeight: '80vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Select File{multiSelect ? 's' : ''}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {fileTypeFilter ? `Showing ${fileTypeFilter} files only` : 'Choose from your uploaded files'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* File List */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-red-600">
              <p>Error loading files: {error}</p>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <File className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium">
                {searchQuery ? 'No files match your search' : 'No files available'}
              </p>
              <p className="text-sm mt-2">
                {searchQuery ? 'Try a different search term' : 'Upload files first to select them'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredFiles.map((file) => {
                const FileIcon = getFileIcon(file.mime_type, file.filename);
                const isSelected = isFileSelected(file);

                return (
                  <div
                    key={file.file_id}
                    onClick={() => handleFileClick(file)}
                    className={`
                      relative p-4 border-2 rounded-lg cursor-pointer transition-all
                      ${isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    {/* Selection Indicator */}
                    <div className="absolute top-2 right-2">
                      {isSelected ? (
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-400" />
                      )}
                    </div>

                    {/* File Icon */}
                    <div className="flex items-center gap-3 mb-2">
                      <FileIcon className="w-8 h-8 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate" title={file.filename}>
                          {file.filename}
                        </div>
                      </div>
                    </div>

                    {/* File Details */}
                    <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                      <span>{formatFileSize(file.file_size)}</span>
                      {file.mime_type && (
                        <span className="truncate ml-2" title={file.mime_type}>
                          {file.mime_type.split('/')[0]}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedFiles.length > 0 ? (
              <>
                {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
              </>
            ) : (
              <>Select {multiSelect ? 'one or more files' : 'a file'} to continue</>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedFiles.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {multiSelect ? `Attach ${selectedFiles.length || ''} File${selectedFiles.length !== 1 ? 's' : ''}` : 'Attach File'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
